import { defineStore } from 'pinia'
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { doc, onSnapshot } from 'firebase/firestore'
import { useCustomerStore } from './customer'
import { PaymentMethodEnum, SubscriptionStatus } from '@gh/common/models'
import { paymentMethodFromSnapshot } from '@gh/common/paymentMethod'
import type { PaymentMethod, SubscriptionPlanNamesMap } from '@gh/common/paymentMethod'
import {
  paymentMethodInputCacheDocumentSchema,
  snapshotFromPaymentMethodInputCacheDocument,
  type PaymentMethodInputCacheDocument,
} from '@gh/common/models'
import { firestore } from '@/firebase'
import type { PaymentMethodsStoreType } from '@/services/paymentMethods/types'
import createLogger from '@/utils/logger'
import { runWithLocalStorageThrottle } from '@/utils/localStorageThrottle'
import { PAYMENT_METHOD_RECALC_THROTTLE_KEY_PREFIX } from '@/utils/logoutStorageCleanup'
import { CloudFunctions } from '@/components/cloudFunctions'
import { waitUntilReady } from '@/utils/storeUtils'

const logger = createLogger('stores/paymentMethod.ts')
const RECALC_THROTTLE_WINDOW_MS = 60 * 60 * 1000

const EMPTY_PLAN_NAMES: SubscriptionPlanNamesMap = {
  subscription: null,
  customer: null,
  sponsorInfo: null,
  none: null,
}

/** Shown only while `payment_method_inputs` is not yet available (hydration pending). */
const PAYMENT_METHOD_FALLBACK: PaymentMethod = {
  showPlanSelectionAndStripeCheckout: true,
  neverSubscribedAndNoTrial: false,
  paymentCompleted: false,
  paymentNeeded: true,
  reviewCue: true,
  subscriptionStatus: SubscriptionStatus.Inactive,
  subscriptionActive: false,
  subscriptionFrom: 'none',
  subscriptionPlanName: null,
  subscriptionPlanNames: EMPTY_PLAN_NAMES,
  subscriptionPlanType: null,
  placeholderForSubscriptionInfo: null,
  consultationIncluded: false,
  sponsorEndDate: null,
  daysUntilSponsorEnd: null,
  isInSponsorTransitionWindow: false,
  subscriptionScheduleStatus: null,
  trialStatus: 'none',
  trialStartDate: null,
  trialEndDate: null,
  trialActive: false,
  daysUntilTrialEnd: null,
}

type CacheStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'error'

type AdditionalParams = {
  PaymentMethod: ComputedRef<PaymentMethodEnum | undefined>
  storeId: ComputedRef<string>
  waitForStoreUpdate: (maxAttempts?: number, initialDelay?: number) => Promise<void>
  untilLoadingIsDone: () => Promise<boolean>
  debugStoreObj: Ref<PaymentMethod>
}

export const usePaymentMethodStore = defineStore<
  'paymentMethod',
  PaymentMethodsStoreType & AdditionalParams
>('paymentMethod', () => {
  const customerStore = useCustomerStore()

  const cacheDoc = ref<PaymentMethodInputCacheDocument | null>(null)
  const cacheLoading = ref(false)
  const cacheStatus = ref<CacheStatus>('idle')
  let unsubscribeCache: (() => void) | null = null

  const paymentMethod = computed((): PaymentMethodEnum | undefined => {
    if (!cacheDoc.value) {
      return undefined
    }
    try {
      return snapshotFromPaymentMethodInputCacheDocument(cacheDoc.value).paymentMethodKind
    } catch {
      return undefined
    }
  })

  const triggerRecalc = async () => {
    const customerId = customerStore.customer?.id
    if (!customerId) {
      return
    }

    await runWithLocalStorageThrottle(
      {
        key: `${PAYMENT_METHOD_RECALC_THROTTLE_KEY_PREFIX}${customerId}`,
        throttleWindowMs: RECALC_THROTTLE_WINDOW_MS,
      },
      async () => {
        try {
          await CloudFunctions.Admin.refreshPaymentMethodInputsForCustomer({
            customerId,
          })
        } finally {
          setTimeout(() => {
            if (cacheLoading.value) {
              cacheLoading.value = false
              cacheDoc.value = null
              cacheStatus.value = 'missing'
            }
          }, 2000)
        }
      },
    )
  }

  watch(
    () => customerStore.customer?.id,
    (uid) => {
      if (unsubscribeCache) {
        unsubscribeCache()
        unsubscribeCache = null
      }
      cacheDoc.value = null
      cacheStatus.value = 'idle'
      if (!uid) {
        cacheLoading.value = false
        return
      }
      cacheLoading.value = true
      const refDoc = doc(firestore, 'payment_method_inputs', uid)
      unsubscribeCache = onSnapshot(
        refDoc,
        (snap) => {
          if (!snap.exists()) {
            triggerRecalc()
            return
          }
          cacheLoading.value = false
          if (!snap.exists()) {
            cacheDoc.value = null
            cacheStatus.value = 'missing'
            return
          }
          const parsed = paymentMethodInputCacheDocumentSchema.safeParse({
            id: snap.id,
            ...snap.data(),
          })
          if (!parsed.success) {
            logger.error('payment_method_inputs parse failed', parsed.error)
            cacheDoc.value = null
            cacheStatus.value = 'error'
            return
          }
          cacheDoc.value = parsed.data
          cacheStatus.value = 'ready'
        },
        (err) => {
          cacheLoading.value = false
          cacheDoc.value = null
          cacheStatus.value = 'error'
          logger.error('payment_method_inputs subscription error', err)
        },
      )
    },
    { immediate: true },
  )

  const store = computed((): PaymentMethod => {
    if (cacheDoc.value) {
      try {
        return paymentMethodFromSnapshot(
          snapshotFromPaymentMethodInputCacheDocument(cacheDoc.value),
        )
      } catch (e) {
        logger.error('paymentMethodFromSnapshot failed', e)
      }
    }
    return PAYMENT_METHOD_FALLBACK
  })

  const isLoading = computed(() => {
    if (!customerStore.customer?.id) {
      return customerStore.isLoading
    }
    if (cacheLoading.value) {
      return true
    }
    if (cacheStatus.value === 'ready') {
      return false
    }
    return true
  })

  async function waitForStoreUpdate(maxAttempts = 8, initialDelay = 100) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (!isLoading.value && (store.value.paymentCompleted || !store.value.paymentNeeded)) {
        return
      }
      const delay = initialDelay * 2 ** attempt
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  function untilLoadingIsDone() {
    return waitUntilReady({ continueOnTrue: () => !isLoading.value, timeoutMs: 20_000 })
  }

  return {
    PaymentMethod: paymentMethod,
    storeId: computed(() => 'paymentMethod'),
    showPlanSelectionAndStripeCheckout: computed(
      () => store.value.showPlanSelectionAndStripeCheckout,
    ),
    neverSubscribedAndNoTrial: computed(() => store.value.neverSubscribedAndNoTrial),
    paymentCompleted: computed(() => store.value.paymentCompleted),
    paymentNeeded: computed(() => store.value.paymentNeeded),
    reviewCue: computed(() => store.value.reviewCue),
    subscriptionStatus: computed(() => store.value.subscriptionStatus),
    subscriptionActive: computed(() => store.value.subscriptionActive),
    subscriptionFrom: computed(() => store.value.subscriptionFrom),
    placeholderForSubscriptionInfo: computed(() => store.value.placeholderForSubscriptionInfo),
    consultationIncluded: computed(() => store.value.consultationIncluded),
    sponsorEndDate: computed(() => store.value.sponsorEndDate),
    daysUntilSponsorEnd: computed(() => store.value.daysUntilSponsorEnd),
    isInSponsorTransitionWindow: computed(() => store.value.isInSponsorTransitionWindow),
    subscriptionPlanName: computed(() => store.value.subscriptionPlanName),
    subscriptionPlanNames: computed(() => store.value.subscriptionPlanNames),
    subscriptionPlanType: computed(() => store.value.subscriptionPlanType),
    isLoading: computed(() => isLoading.value),
    debugStoreObj: ref(store.value),
    subscriptionScheduleStatus: computed(() => store.value.subscriptionScheduleStatus),
    trialStatus: computed(() => store.value.trialStatus),
    trialStartDate: computed(() => store.value.trialStartDate),
    trialEndDate: computed(() => store.value.trialEndDate),
    trialActive: computed(() => store.value.trialActive),
    daysUntilTrialEnd: computed(() => store.value.daysUntilTrialEnd),

    waitForStoreUpdate,
    untilLoadingIsDone,
  } satisfies AdditionalParams & PaymentMethodsStoreType
})
